import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';

import Loader from '~/components/Loader/Loader';
import { api2 } from '~/services/api';

import handleCommunityError from './communityError';
import {
  buildCommunityUpdatePayload,
  COMMUNITY_TYPES,
  normalizeCommunityPayload,
  validateCommunity,
} from './communityUtils';

const EMPTY_COMMUNITY = {
  name: '',
  description: '',
  image: null,
  type: '',
  order: 0,
  race_calendar_id: null,
  city: '',
  state: '',
  url: '',
};

export default function CommunityFormModal({
  community,
  raceCalendarName,
  open,
  toggle,
  onSaved,
}) {
  const dispatch = useDispatch();
  const [values, setValues] = useState(EMPTY_COMMUNITY);
  const [initialValues, setInitialValues] = useState(EMPTY_COMMUNITY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [loadingCommunity, setLoadingCommunity] = useState(false);
  const [raceCalendars, setRaceCalendars] = useState([]);
  const [raceCalendarError, setRaceCalendarError] = useState(false);

  const isEditing = Boolean(community && community.id);

  useEffect(() => {
    let mounted = true;
    const baseValues = community
      ? normalizeCommunityPayload(community)
      : EMPTY_COMMUNITY;

    setValues(baseValues);
    setInitialValues(baseValues);
    setErrors({});
    setRaceCalendarError(false);

    async function loadFormData() {
      const requests = [
        api2.get('/race_calendar', { params: { skip: 0, limit: 100 } }),
      ];

      if (isEditing) {
        setLoadingCommunity(true);
        requests.push(api2.get(`/communities/${community.id}`));
      }

      const results = await Promise.allSettled(requests);
      if (!mounted) return;

      const raceResult = results[0];
      if (raceResult.status === 'fulfilled') {
        const responseData = raceResult.value.data;
        setRaceCalendars(
          responseData && Array.isArray(responseData.data)
            ? responseData.data
            : []
        );
      } else {
        setRaceCalendarError(true);
        if (
          raceResult.reason &&
          raceResult.reason.response &&
          raceResult.reason.response.status === 401
        ) {
          handleCommunityError(raceResult.reason, dispatch);
        }
      }

      if (isEditing) {
        const communityResult = results[1];
        if (communityResult.status === 'fulfilled') {
          const loadedValues = normalizeCommunityPayload(
            communityResult.value.data
          );
          setValues(loadedValues);
          setInitialValues(loadedValues);
        } else {
          handleCommunityError(
            communityResult.reason,
            dispatch,
            'Não foi possível carregar a comunidade.'
          );
          toggle();
        }
        setLoadingCommunity(false);
      }
    }

    loadFormData();

    return () => {
      mounted = false;
    };
  }, [community, dispatch, isEditing, toggle]);

  function setField(field, value) {
    setValues(current => ({ ...current, [field]: value }));
    setErrors(current => ({ ...current, [field]: undefined }));
  }

  async function handleImageUpload(event) {
    if (!event.target.files.length) return;

    const file = event.target.files[0];
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      toast.warn('Envie uma imagem nos formatos JPEG, PNG ou WebP.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.warn('A imagem deve ter no máximo 5 MB.');
      return;
    }

    const imageData = new FormData();
    imageData.append('file', file);

    setImageLoading(true);
    try {
      const response = await api2.post('/file/community-image', imageData);

      if (response.data && response.data.Location) {
        setField('image', response.data.Location);
      } else {
        toast.error('Não foi possível obter o endereço da imagem enviada.');
      }
    } catch (error) {
      handleCommunityError(
        error,
        dispatch,
        'Não foi possível enviar a imagem da comunidade.'
      );
    } finally {
      setImageLoading(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const validationErrors = validateCommunity(values);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = normalizeCommunityPayload(values);
    const requestPayload = isEditing
      ? buildCommunityUpdatePayload(initialValues, payload)
      : payload;

    if (isEditing && Object.keys(requestPayload).length === 0) {
      toast.info('Nenhuma alteração para salvar.');
      toggle();
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await api2.put(`/communities/${community.id}`, requestPayload);
        toast.success('Comunidade atualizada com sucesso.');
      } else {
        await api2.post('/communities', requestPayload);
        toast.success('Comunidade criada com sucesso.');
      }

      onSaved();
      toggle();
    } catch (error) {
      handleCommunityError(
        error,
        dispatch,
        isEditing
          ? 'Não foi possível atualizar a comunidade.'
          : 'Não foi possível criar a comunidade.'
      );
    } finally {
      setLoading(false);
    }
  }

  const currentRaceId = values.race_calendar_id || '';
  const currentRaceIsMissing =
    currentRaceId &&
    !raceCalendars.some(race => race._id === currentRaceId);

  return (
    <Modal isOpen={open} toggle={toggle} size="lg">
      <Form onSubmit={handleSubmit} noValidate>
        <ModalHeader toggle={toggle}>
          {isEditing ? 'Editar comunidade' : 'Nova comunidade'}
        </ModalHeader>
        <ModalBody>
          {(loading || loadingCommunity) && <Loader />}

          <Row form>
            <Col md={6}>
              <FormGroup>
                <Label for="community-name">Nome</Label>
                <Input
                  id="community-name"
                  value={values.name}
                  onChange={event => setField('name', event.target.value)}
                  invalid={Boolean(errors.name)}
                  disabled={loadingCommunity}
                />
                <FormFeedback>{errors.name}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="community-type">Tipo</Label>
                <Input
                  id="community-type"
                  type="select"
                  value={values.type}
                  onChange={event => setField('type', event.target.value)}
                  invalid={Boolean(errors.type)}
                  disabled={loadingCommunity}
                >
                  <option value="">Selecione</option>
                  {COMMUNITY_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{errors.type}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="community-order">Ordem</Label>
                <Input
                  id="community-order"
                  type="number"
                  min="0"
                  step="1"
                  value={values.order}
                  onChange={event => setField('order', event.target.value)}
                  invalid={Boolean(errors.order)}
                  disabled={loadingCommunity}
                />
                <FormFeedback>{errors.order}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="community-description">Descrição</Label>
            <Input
              id="community-description"
              type="textarea"
              rows={4}
              value={values.description}
              onChange={event => setField('description', event.target.value)}
              invalid={Boolean(errors.description)}
              disabled={loadingCommunity}
            />
            <FormFeedback>{errors.description}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label for="community-image">Imagem (opcional)</Label>
            <Input
              id="community-image"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={loadingCommunity || imageLoading}
            />
            <FormText color="muted">
              Formatos JPEG, PNG ou WebP, com no máximo 5 MB.
            </FormText>
            {imageLoading && <Loader />}
            {values.image && !imageLoading && (
              <div className="community-image-preview-container">
                <img
                  className="community-image-preview"
                  src={values.image}
                  alt={`Imagem da comunidade ${values.name || ''}`}
                />
                <Button
                  type="button"
                  color="danger"
                  size="sm"
                  onClick={() => setField('image', null)}
                >
                  Remover imagem
                </Button>
              </div>
            )}
          </FormGroup>

          <Row form>
            <Col md={8}>
              <FormGroup>
                <Label for="community-city">Cidade</Label>
                <Input
                  id="community-city"
                  value={values.city}
                  onChange={event => setField('city', event.target.value)}
                  invalid={Boolean(errors.city)}
                  disabled={loadingCommunity}
                />
                <FormFeedback>{errors.city}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="community-state">Estado</Label>
                <Input
                  id="community-state"
                  value={values.state}
                  onChange={event => setField('state', event.target.value)}
                  invalid={Boolean(errors.state)}
                  disabled={loadingCommunity}
                />
                <FormFeedback>{errors.state}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="community-url">URL</Label>
            <Input
              id="community-url"
              type="url"
              placeholder="https://example.com/comunidade"
              value={values.url}
              onChange={event => setField('url', event.target.value)}
              invalid={Boolean(errors.url)}
              disabled={loadingCommunity}
            />
            <FormFeedback>{errors.url}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label for="community-race-calendar">
              Corrida vinculada (opcional)
            </Label>
            <Input
              id="community-race-calendar"
              type="select"
              value={currentRaceId}
              onChange={event =>
                setField('race_calendar_id', event.target.value || null)
              }
              invalid={Boolean(errors.race_calendar_id)}
              disabled={loadingCommunity}
            >
              <option value="">Nenhuma corrida</option>
              {currentRaceIsMissing && (
                <option value={currentRaceId}>
                  {raceCalendarName || 'Corrida vinculada'}
                </option>
              )}
              {raceCalendars.map(race => (
                <option key={race._id} value={race._id}>
                  {race.name}
                </option>
              ))}
            </Input>
            <FormFeedback>{errors.race_calendar_id}</FormFeedback>
            {raceCalendarError && (
              <small className="text-warning">
                Não foi possível carregar as corridas. O vínculo permanece
                opcional.
              </small>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={toggle}
            disabled={loading || imageLoading}
          >
            Cancelar
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={loadingCommunity || loading || imageLoading}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
